/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { metaTagKey } from '@/constants/seoMetaTags';
import { useLocale } from '@/hooks/useLocale';
import Head from 'next/head';

export type ProductDetailMetadataProps = {
  title: string | undefined;
  metaTitle: string | undefined;
  url: string | undefined;
  description: string | undefined;
  image: string | undefined;
  siteName: string | undefined;
  canonicalURL: string | undefined;
  alternateURL: string | undefined;
  hrefLang: string | undefined;
};

export function ProductDetailMetadata({
  title,
  metaTitle,
  url,
  description,
  image,
  siteName,
  canonicalURL,
  alternateURL,
  hrefLang,
}: ProductDetailMetadataProps) {
  const locale = useLocale();
  return (
    <Head>
      <title key={metaTagKey('title')}>{title}</title>
      {metaTitle && <meta key={metaTagKey('og:title')} name="og:title" content={metaTitle} />}
      {url && <meta key={metaTagKey('og:url')} property="og:url" content={url} />}
      {description && (
        <meta key={metaTagKey('description')} name="description" content={description} />
      )}
      {image && <meta key={metaTagKey('og:image')} property="og:image" content={image} />}
      {siteName && (
        <meta key={metaTagKey('og:site_name')} property="og:site_name" content={siteName} />
      )}
      <meta key={metaTagKey('og:type')} property="og:type" content="product" />
      {canonicalURL && (
        <>
          <link key={metaTagKey('canonical')} rel="canonical" href={canonicalURL} />
          <link
            key={`${metaTagKey('alternateUrl')}-${locale.toLowerCase()}`}
            rel="alternate"
            hrefLang={locale.toLowerCase()}
            href={canonicalURL}
          />
          {alternateURL && (
            <link
              key={`${metaTagKey('alternateUrl')}-${hrefLang}`}
              rel="alternate"
              hrefLang={hrefLang || ''}
              href={alternateURL}
            />
          )}
        </>
      )}
    </Head>
  );
}
